import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Briefcase, Mail, Phone, MapPin, ChevronRight, Loader2, AlertCircle, RefreshCw, MessageSquare } from 'lucide-react';

const COLUMNS = [
  { key: 'NEW',         label: 'New',        color: '#6366F1', bg: 'rgba(99,102,241,0.08)' },
  { key: 'CONTACTED',  label: 'Contacted',   color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  { key: 'QUALIFIED',  label: 'Qualified',   color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
  { key: 'CLOSED_WON', label: 'Closed Won',  color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  { key: 'CLOSED_LOST',label: 'Closed Lost', color: '#6B7280', bg: 'rgba(107,114,128,0.08)' },
];

const MODEL_LABELS = { STORE: '🏪 Store', VAN: '🚐 Van', POD: '📦 Pod' };

export default function DenLeads() {
  const addToast = useToast();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [moving, setMoving] = useState(null);

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/franchise/leads');
      setLeads(data);
    } catch (e) {
      console.error('Failed to load leads:', e);
      addToast(e.message || 'Failed to load leads', 'error');
    }
    setLoading(false);
  };

  const moveToStatus = async (leadId, newStatus) => {
    setMoving(leadId);
    try {
      const updated = await apiFetch(`/api/franchise/leads/${leadId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      setLeads(prev => prev.map(l => l.id === leadId ? updated : l));
      if (selected?.id === leadId) setSelected(updated);
      addToast(`Lead moved to ${newStatus.replace('_', ' ')}`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
    setMoving(null);
  };

  const getLeadsForColumn = (colKey) => leads.filter(l => l.status === colKey);

  return (
    <div data-testid="den-leads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Franchise Leads</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{leads.length} total enquiries</p>
        </div>
        <button onClick={loadLeads} className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-all">
          <RefreshCw size={18} style={{ color: 'var(--text-secondary)' }} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={32} className="animate-spin text-petfolk-orange" /></div>
      ) : leads.length === 0 ? (
        <div className="petfolk-card p-12 text-center">
          <Briefcase size={48} className="mx-auto mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.4 }} />
          <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>No franchise leads yet</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Leads from the Franchise page will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {COLUMNS.map(col => {
              const colLeads = getLeadsForColumn(col.key);
              return (
                <div key={col.key} className="w-72 flex-shrink-0">
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                    <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{col.label}</span>
                    <span className="ml-auto text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: col.bg, color: col.color }}>{colLeads.length}</span>
                  </div>
                  <div className="space-y-3 min-h-24">
                    {colLeads.map(lead => (
                      <div
                        key={lead.id}
                        className="petfolk-card p-4 cursor-pointer hover:shadow-md transition-all"
                        style={{ borderLeft: `3px solid ${col.color}` }}
                        onClick={() => setSelected(lead)}
                        data-testid={`lead-${lead.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{lead.name}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: col.bg, color: col.color }}>{MODEL_LABELS[lead.franchiseModel] || '—'}</span>
                        </div>
                        <p className="text-xs flex items-center gap-1 mb-1" style={{ color: 'var(--text-secondary)' }}><MapPin size={10} /> {lead.city}</p>
                        <p className="text-xs flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><Phone size={10} /> {lead.phone}</p>
                        <p className="text-xs mt-2" style={{ color: 'var(--text-secondary)' }}>
                          {new Date(lead.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Lead Detail Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{selected.name}</h2>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: COLUMNS.find(c=>c.key===selected.status)?.bg, color: COLUMNS.find(c=>c.key===selected.status)?.color }}>
                  {selected.status.replace('_', ' ')}
                </span>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            <div className="space-y-3 text-sm mb-6">
              <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Mail size={14} /> {selected.email}</div>
              <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Phone size={14} /> {selected.phone}</div>
              <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><MapPin size={14} /> {selected.city}</div>
              {selected.franchiseModel && <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Briefcase size={14} /> {MODEL_LABELS[selected.franchiseModel]}</div>}
              {selected.investmentBudget && <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>💰 Budget: {selected.investmentBudget}</div>}
              {selected.message && (
                <div className="p-3 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>MESSAGE</p>
                  <p style={{ color: 'var(--text-primary)' }}>{selected.message}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>MOVE TO</p>
              <div className="flex flex-wrap gap-2">
                {COLUMNS.filter(c => c.key !== selected.status).map(col => (
                  <button
                    key={col.key}
                    onClick={() => moveToStatus(selected.id, col.key)}
                    disabled={moving === selected.id}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90 flex items-center gap-1"
                    style={{ background: col.bg, color: col.color, border: `1px solid ${col.color}30` }}
                  >
                    {moving === selected.id ? <Loader2 size={10} className="animate-spin" /> : <ChevronRight size={10} />}
                    {col.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
